import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Plus, CheckCircle, XCircle, Clock, Calendar, TreePalm, Download, FileText, BookOpen, Wallet, ShieldCheck, Edit2, Trash2, AlertTriangle, Archive, ChevronLeft, ChevronRight, CalendarCheck } from 'lucide-react';
import { toast } from 'sonner';
import { downloadCSV, formatPermissionDuration } from '../lib/exportUtils';
import { useActiveCompany } from '../hooks/useActiveCompany';
import LeaveBalances from './LeaveBalances';
import LeavePolicy from './LeavePolicy';
import UnifiedPolicyManagement from './UnifiedPolicyManagement';
import PermissionCreditCalendar from '../components/PermissionCreditCalendar';
import CalendarValidationPolicyManager from '../components/CalendarValidationPolicyManager';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const isObjectId = (str) => typeof str === 'string' && /^[0-9a-fA-F]{24}$/.test(str);

const formatEmployeeDisplay = (item) => {
  if (!item) return 'Employee Details Unavailable';
  if (item.employee_display && !isObjectId(item.employee_display)) return item.employee_display;

  const name = item.employee_name && !isObjectId(item.employee_name) ? item.employee_name : (item.employee?.name && !isObjectId(item.employee.name) ? item.employee.name : null);
  const code = item.employee_code && !isObjectId(item.employee_code) ? item.employee_code : (item.employee?.employee_id && !isObjectId(item.employee.employee_id) ? item.employee.employee_id : null);

  if (name && code) return `${name} (${code})`;
  if (name) return name;
  if (code) return code;
  return 'Employee Details Unavailable';
};

const formatLeaveTypeDisplay = (item) => {
  if (!item) return 'Leave Type Details Unavailable';
  if (item.leave_type_display && !isObjectId(item.leave_type_display)) return item.leave_type_display;

  const name = item.leave_type_name && !isObjectId(item.leave_type_name) ? item.leave_type_name : (item.leave_type_object?.name && !isObjectId(item.leave_type_object.name) ? item.leave_type_object.name : null);
  const code = item.leave_type_code && !isObjectId(item.leave_type_code) ? item.leave_type_code : (item.leave_type && !isObjectId(item.leave_type) ? item.leave_type : null);

  if (name && code && code !== name) return `${name} (${code})`;
  if (name) return name;
  if (code) return code;
  return 'Leave Type Details Unavailable';
};

const LeaveManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab') || 'applications';
  const VALID_TABS = ['applications', 'permissions', 'permission-calendar', 'calendar-validation-policy', 'leave-types', 'balances', 'policy', 'policy-master'];
  const activeTab = VALID_TABS.includes(rawTab) ? rawTab : 'applications';

  const tabBarRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkScroll = useCallback(() => {
    if (tabBarRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tabBarRef.current;
      setCanScrollLeft(scrollLeft > 6);
      setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 6);
    }
  }, []);

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, [checkScroll]);

  const scrollTabs = (direction) => {
    if (tabBarRef.current) {
      const scrollAmount = direction === 'left' ? -280 : 280;
      tabBarRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      setTimeout(checkScroll, 320);
    }
  };

  useEffect(() => {
    if (tabBarRef.current) {
      const activeEl = tabBarRef.current.querySelector(`[data-testid="tab-${activeTab}"]`);
      if (activeEl) {
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
      setTimeout(checkScroll, 350);
    }
  }, [activeTab, checkScroll]);

  const handleTabChange = (newTab) => {
    setSearchParams({ tab: newTab });
  };

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [applications, setApplications] = useState([]);
  const [balances, setBalances] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, lt: null, deps: null });
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [typeForm, setTypeForm] = useState({ code: '', name: '', leave_category: 'paid', requires_balance: true, annual_quota: 12, carry_forward_limit: 0, allow_negative_balance: false, is_active: true, effective_from: new Date().toISOString().split('T')[0] });
  const [applyForm, setApplyForm] = useState({ employee_id: '', leave_type_id: '', from_date: '', to_date: '', total_days: 1, reason: '', is_half_day: false, half_day: false, half_day_period: 'first_half' });


  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data);
      setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);

  const fetchApplications = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/leave/applications?company_id=${selectedCompany}`, { withCredentials: true });
      setApplications(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) {
      console.error('Failed to fetch leave applications:', e);
      toast.error(e.response?.data?.detail || 'Failed to load leave applications');
    }
  }, [selectedCompany]);

  const fetchPermissions = useCallback(async () => {
    if (!selectedCompany) return;
    try {
      const { data } = await axios.get(`${API_URL}/leave/permissions?company_id=${selectedCompany}`, { withCredentials: true });
      setPermissions(Array.isArray(data) ? data : (data?.data || []));
    } catch (e) {
      console.error('Failed to fetch permissions:', e);
    }
  }, [selectedCompany]);

  const fetchEmployees = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/employees/lookup?limit=300`, { withCredentials: true });
      setEmployees(data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchLeaveTypes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/leave/types?company_id=${selectedCompany}`, { withCredentials: true });
      setLeaveTypes(data);
    } catch (e) { console.error(e); }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      fetchLeaveTypes();
      fetchApplications();
      fetchPermissions();
      fetchEmployees();
    }
  }, [selectedCompany, fetchApplications, fetchPermissions, fetchEmployees, fetchLeaveTypes]);

  const handleOpenCreate = () => {
    setEditingType(null);
    setTypeForm({
      code: '',
      name: '',
      leave_category: 'paid',
      requires_balance: true,
      annual_quota: 12,
      carry_forward_limit: 0,
      allow_negative_balance: false,
      is_active: true,
      effective_from: new Date().toISOString().split('T')[0]
    });
    setShowTypeModal(true);
  };

  const handleOpenEdit = (lt) => {
    setEditingType(lt);
    const reqBal = lt.requires_balance !== undefined
      ? !!lt.requires_balance
      : (lt.balance_required !== undefined ? !!lt.balance_required : (lt.code !== 'LOP' && lt.leave_category !== 'unpaid'));
    setTypeForm({
      code: lt.code || '',
      name: lt.name || '',
      leave_category: lt.leave_category || 'paid',
      requires_balance: reqBal,
      annual_quota: lt.annual_quota !== undefined ? lt.annual_quota : 12,
      carry_forward_limit: lt.carry_forward_limit !== undefined ? lt.carry_forward_limit : (lt.max_carry_forward || 0),
      allow_negative_balance: !!lt.allow_negative_balance,
      is_active: lt.is_active !== false,
      effective_from: lt.effective_from ? lt.effective_from.split('T')[0] : new Date().toISOString().split('T')[0]
    });
    setShowTypeModal(true);
  };

  const handleSaveType = async (e) => {
    e.preventDefault();
    try {
      if (editingType) {
        await axios.patch(`${API_URL}/leave/types/${editingType.id}`, typeForm, { withCredentials: true });
        toast.success(`Leave type '${typeForm.code}' updated successfully`);
      } else {
        await axios.post(`${API_URL}/leave/types`, { ...typeForm, company_id: selectedCompany }, { withCredentials: true });
        toast.success(`Leave type '${typeForm.code}' created successfully`);
      }
      setShowTypeModal(false);
      setEditingType(null);
      fetchLeaveTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || 'Failed to save leave type');
    }
  };

  const handleToggleStatus = async (lt) => {
    const newActive = !lt.is_active;
    const actionLabel = newActive ? 'activated' : 'deactivated';
    try {
      // Optimistically update
      setLeaveTypes(prev => prev.map(t => t.id === lt.id ? { ...t, is_active: newActive, status: newActive ? 'Active' : 'Inactive' } : t));
      await axios.patch(`${API_URL}/leave/types/${lt.id}`, { is_active: newActive }, { withCredentials: true });
      toast.success(`Leave type '${lt.code}' ${actionLabel} successfully`);
      fetchLeaveTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to update status for leave type '${lt.code}'`);
      fetchLeaveTypes();
    }
  };

  const handleDeleteClick = async (lt) => {
    try {
      const { data } = await axios.get(`${API_URL}/leave/types/${lt.id}/dependencies`, { withCredentials: true });
      if (data.has_dependencies) {
        setDeleteModal({ show: true, lt, deps: data });
      } else {
        if (window.confirm(`Are you sure you want to permanently delete unused leave type '${lt.name} (${lt.code})'?`)) {
          await axios.delete(`${API_URL}/leave/types/${lt.id}`, { withCredentials: true });
          toast.success(`Leave type '${lt.name}' deleted successfully`);
          fetchLeaveTypes();
        }
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to inspect leave type dependencies');
    }
  };

  const handleArchiveFromModal = async () => {
    if (!deleteModal.lt) return;
    try {
      await axios.post(`${API_URL}/leave/types/${deleteModal.lt.id}/archive`, {}, { withCredentials: true });
      toast.success(`Leave type '${deleteModal.lt.name}' safely archived`);
      setDeleteModal({ show: false, lt: null, deps: null });
      fetchLeaveTypes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to archive leave type');
    }
  };


  const handleApply = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/leave/applications`, applyForm, { withCredentials: true });
      toast.success('Leave application submitted');
      setShowApplyModal(false);
      fetchApplications();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to apply leave'); }
  };

  const handleApproval = async (appId, status) => {
    try {
      await axios.patch(`${API_URL}/leave/applications/${appId}`, { status }, { withCredentials: true });
      toast.success(`Leave ${status}`);
      fetchApplications();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handlePermissionApproval = async (permId, status) => {
    try {
      await axios.patch(`${API_URL}/leave/permissions/${permId}`, { status }, { withCredentials: true });
      toast.success(`Permission ${status}`);
      fetchPermissions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleAccrual = async () => {
    const month = new Date().getMonth() + 1;
    const year = new Date().getFullYear();
    try {
      const { data } = await axios.post(`${API_URL}/leave/accrual/run?company_id=${selectedCompany}&month=${month}&year=${year}`, {}, { withCredentials: true });
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Accrual failed'); }
  };

  const statusColors = {
    pending: 'bg-amber-100 text-amber-700', pending_manager: 'bg-amber-100 text-amber-700', pending_hr: 'bg-orange-100 text-orange-700',
    approved: 'bg-green-100 text-green-700', rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-500',
  };

  const TAB_ITEMS = [
    { id: 'applications', label: 'Applications', icon: TreePalm },
    { id: 'permissions', label: 'Permissions', icon: Clock },
    { id: 'permission-calendar', label: 'Permission Calendar', icon: Calendar },
    { id: 'calendar-validation-policy', label: 'Calendar & Date Validation Policy', icon: CalendarCheck },
    { id: 'leave-types', label: 'Leave Types', icon: FileText },
    { id: 'balances', label: 'Leave Balances', icon: Wallet },
    { id: 'policy', label: 'Leave Policy', icon: BookOpen },
    { id: 'policy-master', label: 'Policy Master', icon: ShieldCheck },
  ];

  return (
    <MainLayout>
      <div data-testid="leave-management-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Leave &amp; Permission Management</h1>
            <p className="text-sm text-slate-500 mt-1">Unified leave applications, permissions, policy rules, and cycle balances</p>
          </div>
          <div className="flex items-center space-x-3 flex-wrap gap-2">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white font-medium">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button data-testid="run-accrual-btn" onClick={handleAccrual} className="text-sm text-[#0052FF] hover:underline font-medium">Run Accrual</button>
            <button data-testid="leave-export-btn" onClick={() => {
              if (activeTab === 'permissions') {
                window.open(`${API_URL}/leave/permissions/export`, '_blank');
                return;
              }
              const exportData = activeTab === 'applications' ? applications.map(a => ({
                Employee: formatEmployeeDisplay(a), Employee_ID: a.employee_code || a.employee?.employee_id || '', Leave_Type: formatLeaveTypeDisplay(a), From: a.from_date, To: a.to_date, Days: a.total_days, Status: a.status, Reason: a.reason
              })) : activeTab === 'balances' ? balances.map(b => ({
                Employee: formatEmployeeDisplay(b), Employee_ID: b.employee_code || b.employee_id, Leave_Type: formatLeaveTypeDisplay(b), Opening: b.opening_balance, Accrued: b.accrued, Used: b.used, Available: b.available_balance
              })) : leaveTypes.map(t => ({
                Code: t.code, Name: t.name, Annual_Quota: t.annual_quota, Carry_Forward: t.carry_forward_limit, Active: t.is_active
              }));
              if (exportData.length === 0) { toast.error('No data to export'); return; }
              downloadCSV(exportData, `leave_${activeTab}.csv`);
              toast.success(`${activeTab} exported`);
            }} className="flex items-center gap-1 px-3 py-1.5 bg-slate-600 hover:bg-slate-700 text-white rounded-lg text-sm font-medium">
              <Download className="w-3.5 h-3.5" /> Export
            </button>
          </div>
        </div>

        {/* Responsive, Non-Overlapping Tabs Navigation */}
        <div className="relative bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5 flex items-center">
          {/* Scroll Left Button */}
          {canScrollLeft && (
            <button
              type="button"
              onClick={() => scrollTabs('left')}
              className="absolute left-1.5 z-10 p-1.5 rounded-xl bg-white/95 text-slate-700 shadow-md border border-slate-200 hover:bg-slate-50 transition-all flex items-center justify-center shrink-0 cursor-pointer"
              title="Scroll left"
              aria-label="Scroll left"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}

          {/* Scrollable Tabs Track */}
          <div
            ref={tabBarRef}
            onScroll={checkScroll}
            className={`flex items-center gap-1.5 overflow-x-auto scroll-smooth w-full py-0.5 px-0.5 scrollbar-thin ${canScrollLeft ? 'pl-9' : ''} ${canScrollRight ? 'pr-9' : ''}`}
            style={{
              scrollbarWidth: 'thin',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {TAB_ITEMS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              const isCalendarPolicy = tab.id === 'calendar-validation-policy';

              return (
                <button
                  key={tab.id}
                  data-testid={`tab-${tab.id}`}
                  onClick={() => handleTabChange(tab.id)}
                  className={`shrink-0 py-2.5 px-4 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-150 flex items-center gap-2 whitespace-nowrap select-none cursor-pointer ${
                    isActive
                      ? isCalendarPolicy
                        ? 'bg-indigo-600 text-white shadow-md shadow-indigo-600/25 ring-1 ring-indigo-500 font-bold'
                        : 'bg-blue-600 text-white shadow-md shadow-blue-600/25 ring-1 ring-blue-500 font-bold'
                      : isCalendarPolicy
                      ? 'text-indigo-700 bg-indigo-50/80 hover:bg-indigo-100/90 border border-indigo-200/70 font-semibold'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80 border border-transparent font-medium'
                  }`}
                >
                  <Icon className={`w-4 h-4 shrink-0 ${
                    isActive
                      ? 'text-white'
                      : isCalendarPolicy
                      ? 'text-indigo-600'
                      : 'text-slate-400'
                  }`} />
                  <span>{tab.label}</span>
                  {isCalendarPolicy && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-md font-bold uppercase tracking-wider ${
                      isActive ? 'bg-indigo-700/90 text-white' : 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                    }`}>
                      Calendar
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Scroll Right Button */}
          {canScrollRight && (
            <button
              type="button"
              onClick={() => scrollTabs('right')}
              className="absolute right-1.5 z-10 p-1.5 rounded-xl bg-white/95 text-slate-700 shadow-md border border-slate-200 hover:bg-slate-50 transition-all flex items-center justify-center shrink-0 cursor-pointer"
              title="Scroll right"
              aria-label="Scroll right"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Tab 1: Applications */}
        {activeTab === 'applications' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-800">Leave Applications</h2>
              <button data-testid="apply-leave-btn" onClick={() => setShowApplyModal(true)} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC]">
                <Plus className="w-4 h-4" /><span>Apply Leave</span>
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Employee</th>
                <th className="px-4 py-3 text-left font-medium">Type</th>
                <th className="px-4 py-3 text-left font-medium">From</th>
                <th className="px-4 py-3 text-left font-medium">To</th>
                <th className="px-4 py-3 text-left font-medium">Days</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {applications.map((a) => (
                  <tr key={a.id} data-testid={`leave-app-row-${a.id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-800 font-medium">{formatEmployeeDisplay(a)}</td>
                    <td className="px-4 py-3 text-slate-600 font-medium">{formatLeaveTypeDisplay(a)}</td>
                    <td className="px-4 py-3 text-slate-600">{a.from_date}</td>
                    <td className="px-4 py-3 text-slate-600">{a.to_date}</td>
                    <td className="px-4 py-3">
                      {a.is_permission || a.leave_type_code === 'ONE_HOUR_PERMISSION' || a.leave_type_id === 'ONE_HOUR_PERMISSION' ? (
                        <span className="text-indigo-800 font-medium bg-indigo-50 px-2 py-0.5 rounded border border-indigo-200 text-xs inline-block">
                          {a.duration_display || formatPermissionDuration(a.duration_minutes || (a.duration_hours ? a.duration_hours * 60 : 60))}
                        </span>
                      ) : a.is_partial_day || a.half_day ? (
                        <span className="text-amber-800 font-medium bg-amber-50 px-2 py-0.5 rounded border border-amber-200 text-xs inline-block">
                          0.5 Day {a.half_day_period ? `(${a.half_day_period === 'first_half' ? 'First Half' : 'Second Half'})` : '(Half Day)'}
                        </span>
                      ) : (
                        `${a.total_days !== undefined && a.total_days !== null ? a.total_days : (a.days || 1)} Day(s)`
                      )}
                    </td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[a.status] || 'bg-slate-100'}`}>{a.status?.replace('_', ' ')}</span></td>
                    <td className="px-4 py-3">
                      {(a.status === 'pending_manager' || a.status === 'pending_hr' || a.status === 'pending' || a.status === 'approved') && (
                        <div className="flex items-center space-x-1">
                          {a.status !== 'approved' && (
                            <>
                              <button data-testid={`approve-leave-${a.id}`} onClick={() => handleApproval(a.id, 'approved')} className="text-green-600 hover:text-green-800" title="Approve"><CheckCircle className="w-4 h-4" /></button>
                              <button data-testid={`reject-leave-${a.id}`} onClick={() => handleApproval(a.id, 'rejected')} className="text-red-600 hover:text-red-800" title="Reject"><XCircle className="w-4 h-4" /></button>
                            </>
                          )}
                          <button data-testid={`cancel-leave-${a.id}`} onClick={() => handleApproval(a.id, 'cancelled')} className="text-slate-500 hover:text-slate-700" title="Cancel Application"><XCircle className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {applications.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No leave applications</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 2: Permissions */}
        {activeTab === 'permissions' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <h2 className="text-base font-semibold text-slate-800">Permission Applications</h2>
              <button 
                onClick={() => window.open(`${API_URL}/leave/permissions/export`, '_blank')} 
                className="flex items-center space-x-2 bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-emerald-700"
              >
                <Download className="w-4 h-4" /><span>Export Permission Register</span>
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Employee</th>
                <th className="px-4 py-3 text-left font-medium">Permission Date</th>
                <th className="px-4 py-3 text-left font-medium">Time Range</th>
                <th className="px-4 py-3 text-left font-medium">Duration</th>
                <th className="px-4 py-3 text-left font-medium">Reason</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {permissions.map((p) => (
                  <tr key={p.id} data-testid={`permission-row-${p.id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-slate-800 font-medium">{formatEmployeeDisplay(p)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-700">{p.permission_date}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600">{p.start_time} - {p.end_time}</td>
                    <td className="px-4 py-3 font-semibold text-slate-700">
                      {p.duration_display || formatPermissionDuration(p.duration_minutes || 60)}
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-xs truncate" title={p.reason}>{p.reason}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-medium uppercase ${statusColors[p.status] || 'bg-slate-100'}`}>{p.status}</span></td>
                    <td className="px-4 py-3">
                      {['pending', 'pending_manager', 'pending_hr'].includes(p.status) && (
                        <div className="flex items-center space-x-1">
                          <button data-testid={`approve-perm-${p.id}`} onClick={() => handlePermissionApproval(p.id, 'approved')} className="text-green-600 hover:text-green-800" title="Approve"><CheckCircle className="w-4 h-4" /></button>
                          <button data-testid={`reject-perm-${p.id}`} onClick={() => handlePermissionApproval(p.id, 'rejected')} className="text-red-600 hover:text-red-800" title="Reject"><XCircle className="w-4 h-4" /></button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
                {permissions.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No permission applications found</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 2.5: Permission Credit Calendar */}
        {activeTab === 'permission-calendar' && (
          <PermissionCreditCalendar selectedCompany={selectedCompany} />
        )}

        {/* Tab 2.6: Calendar & Date Validation Policy */}
        {activeTab === 'calendar-validation-policy' && (
          <CalendarValidationPolicyManager selectedCompany={selectedCompany} />
        )}

        {/* Tab 3: Leave Types */}
        {activeTab === 'leave-types' && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
              <div>
                <h2 className="text-base font-semibold text-slate-800">Leave Types</h2>
                <p className="text-xs text-slate-500 mt-0.5">Manage policy categories, annual quotas, carry-forward, and active availability</p>
              </div>
              <button data-testid="add-leave-type-btn" onClick={handleOpenCreate} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC]">
                <Plus className="w-4 h-4" /><span>Add Type</span>
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600"><tr>
                <th className="px-4 py-3 text-left font-medium">Code</th>
                <th className="px-4 py-3 text-left font-medium">Name</th>
                <th className="px-4 py-3 text-left font-medium">Category</th>
                <th className="px-4 py-3 text-left font-medium">Balance Req.</th>
                <th className="px-4 py-3 text-left font-medium">Annual Quota</th>
                <th className="px-4 py-3 text-left font-medium">Carry Forward</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-right font-medium">Actions</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {leaveTypes.map((lt) => (
                  <tr key={lt.id} data-testid={`leave-type-row-${lt.id}`} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-800">{lt.code}</td>
                    <td className="px-4 py-3 text-slate-800 font-medium">{lt.name}</td>
                    <td className="px-4 py-3 capitalize text-slate-600">{lt.leave_category}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        lt.requires_balance === false || lt.balance_required === false || lt.code === 'LOP'
                          ? 'bg-amber-100 text-amber-800 border border-amber-200'
                          : 'bg-blue-50 text-blue-700 border border-blue-200'
                      }`}>
                        {lt.requires_balance === false || lt.balance_required === false || lt.code === 'LOP' ? 'Not Required' : 'Required'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-700">{lt.annual_quota} Day(s)</td>
                    <td className="px-4 py-3 text-slate-600">{lt.carry_forward_limit || lt.max_carry_forward || 0}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${lt.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}>
                        {lt.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          data-testid={`toggle-status-${lt.id}`}
                          onClick={() => handleToggleStatus(lt)}
                          className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${
                            lt.is_active
                              ? 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200'
                              : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                          }`}
                          title={lt.is_active ? 'Deactivate Leave Type' : 'Activate Leave Type'}
                        >
                          {lt.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          data-testid={`edit-leave-type-${lt.id}`}
                          onClick={() => handleOpenEdit(lt)}
                          className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
                          title="Edit Leave Type"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          data-testid={`delete-leave-type-${lt.id}`}
                          onClick={() => handleDeleteClick(lt)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete or Archive Leave Type"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {leaveTypes.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400">No leave types configured</td></tr>}
              </tbody>
            </table>
          </div>
        )}

        {/* Tab 4: Leave Balances */}
        {activeTab === 'balances' && (
          <div className="-mx-6 -my-4">
            <LeaveBalances />
          </div>
        )}

        {/* Tab 5: Leave Policy */}
        {activeTab === 'policy' && (
          <div className="-mx-6 -my-4">
            <LeavePolicy />
          </div>
        )}

        {/* Tab 6: Policy Master */}
        {activeTab === 'policy-master' && (
          <div className="-mx-6 -my-4">
            <UnifiedPolicyManagement />
          </div>
        )}

        {/* Type Create / Edit Modal */}
        {showTypeModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowTypeModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">{editingType ? 'Edit Leave Type' : 'New Leave Type'}</h3>
              <form onSubmit={handleSaveType} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Code *</label><input data-testid="ltype-code-input" value={typeForm.code} onChange={(e) => setTypeForm({ ...typeForm, code: e.target.value.toUpperCase() })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">Name *</label><input data-testid="ltype-name-input" value={typeForm.name} onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Category *</label>
                    <select data-testid="ltype-category-select" value={typeForm.leave_category} onChange={(e) => setTypeForm({ ...typeForm, leave_category: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white" required>
                      <option value="paid">Paid</option>
                      <option value="unpaid">Unpaid</option>
                      <option value="compensatory">Compensatory</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Balance Requirement *</label>
                    <select
                      data-testid="ltype-requires-balance-select"
                      value={typeForm.requires_balance ? 'required' : 'not_required'}
                      onChange={(e) => setTypeForm({ ...typeForm, requires_balance: e.target.value === 'required' })}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                      required
                    >
                      <option value="required">Required</option>
                      <option value="not_required">Not Required (e.g. Loss of Pay)</option>
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Effective From *</label>
                    <input data-testid="ltype-effective-input" type="date" value={typeForm.effective_from} onChange={(e) => setTypeForm({ ...typeForm, effective_from: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Annual Quota</label>
                    <input type="number" min="0" step="0.5" value={typeForm.annual_quota} onChange={(e) => setTypeForm({ ...typeForm, annual_quota: parseFloat(e.target.value) || 0 })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Carry Forward Limit</label>
                  <input type="number" min="0" step="0.5" value={typeForm.carry_forward_limit} onChange={(e) => setTypeForm({ ...typeForm, carry_forward_limit: parseFloat(e.target.value) || 0 })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div className="flex items-center space-x-4 pt-1">
                  <label className="flex items-center space-x-2 cursor-pointer"><input type="checkbox" checked={typeForm.allow_negative_balance} onChange={(e) => setTypeForm({ ...typeForm, allow_negative_balance: e.target.checked })} className="rounded text-[#0052FF]" /><span className="text-sm text-slate-700">Allow Negative Balance</span></label>
                  <label className="flex items-center space-x-2 cursor-pointer"><input type="checkbox" checked={typeForm.is_active} onChange={(e) => setTypeForm({ ...typeForm, is_active: e.target.checked })} className="rounded text-[#0052FF]" /><span className="text-sm text-slate-700 font-semibold">Active Status</span></label>
                </div>
                <div className="flex justify-end space-x-3 pt-3 border-t border-slate-100">
                  <button type="button" onClick={() => setShowTypeModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="save-leave-type-btn" type="submit" className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC] font-semibold">{editingType ? 'Save Changes' : 'Create Leave Type'}</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete / Dependency Warning Modal */}
        {deleteModal.show && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setDeleteModal({ show: false, lt: null, deps: null })}>
            <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start gap-3">
                <div className="p-2.5 rounded-full bg-amber-100 text-amber-700 shrink-0">
                  <AlertTriangle className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900">Cannot Delete: Existing Records Reference This Leave Type</h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Leave type <strong className="text-slate-800">{deleteModal.lt?.name} ({deleteModal.lt?.code})</strong> cannot be destructively deleted because historical transactional records depend on it:
                  </p>
                </div>
              </div>

              <div className="mt-4 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs space-y-1.5 text-slate-700 font-medium">
                {deleteModal.deps?.details?.leave_applications > 0 && (
                  <div className="flex items-center justify-between">
                    <span>• Employee Leave Applications:</span>
                    <span className="font-bold text-slate-900 font-mono">{deleteModal.deps.details.leave_applications} record(s)</span>
                  </div>
                )}
                {deleteModal.deps?.details?.leave_balances > 0 && (
                  <div className="flex items-center justify-between">
                    <span>• Employee Leave Balances:</span>
                    <span className="font-bold text-slate-900 font-mono">{deleteModal.deps.details.leave_balances} record(s)</span>
                  </div>
                )}
                {deleteModal.deps?.details?.attendance_records > 0 && (
                  <div className="flex items-center justify-between">
                    <span>• Attendance Logs:</span>
                    <span className="font-bold text-slate-900 font-mono">{deleteModal.deps.details.attendance_records} record(s)</span>
                  </div>
                )}
                {deleteModal.deps?.details?.leave_policies > 0 && (
                  <div className="flex items-center justify-between">
                    <span>• Policy Configurations:</span>
                    <span className="font-bold text-slate-900 font-mono">{deleteModal.deps.details.leave_policies} rule(s)</span>
                  </div>
                )}
              </div>

              <p className="text-xs text-slate-600 mt-3">
                To protect audit and payroll history integrity, hard deletion is blocked. You can safely <strong>Archive</strong> or <strong>Deactivate</strong> it instead. This preserves historical applications while preventing any new leave requests.
              </p>

              <div className="mt-5 flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setDeleteModal({ show: false, lt: null, deps: null })}
                  className="px-3.5 py-2 text-xs font-semibold text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  data-testid="deactivate-instead-btn"
                  onClick={() => {
                    handleToggleStatus(deleteModal.lt);
                    setDeleteModal({ show: false, lt: null, deps: null });
                  }}
                  className="px-3.5 py-2 text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white rounded-lg transition-colors"
                >
                  Deactivate Instead
                </button>
                <button
                  type="button"
                  data-testid="archive-leave-type-btn"
                  onClick={handleArchiveFromModal}
                  className="px-3.5 py-2 text-xs font-semibold bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-1.5 transition-colors"
                >
                  <Archive className="w-3.5 h-3.5" />
                  <span>Archive Safely</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Apply Modal */}
        {showApplyModal && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowApplyModal(false)}>
            <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-semibold text-slate-900 mb-4">Apply Leave</h3>
              <form onSubmit={handleApply} className="space-y-4">
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Employee *</label>
                  <select data-testid="apply-employee-select" value={applyForm.employee_id} onChange={(e) => setApplyForm({ ...applyForm, employee_id: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required>
                    <option value="">Select Employee</option>
                    {employees.map((emp) => {
                      const name = emp.display_name || emp.name || (emp.personal_details ? `${emp.personal_details.first_name || ''} ${emp.personal_details.last_name || ''}`.trim() : '') || emp.full_name || 'Employee';
                      const code = emp.employee_code || emp.employment_details?.employee_code || '';
                      const label = code ? `${name} (${code})` : name;
                      return <option key={emp.id || emp._id} value={emp.id || emp._id}>{label}</option>;
                    })}
                  </select></div>

                {(() => {
                  const activeTypes = leaveTypes.filter(lt => lt.is_active && !lt.is_deleted);
                  return (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Leave Type *</label>
                      {activeTypes.length === 0 ? (
                        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-xs flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                          <span>No active leave types available. Please contact your HR administrator.</span>
                        </div>
                      ) : (
                        <select data-testid="apply-type-select" value={applyForm.leave_type_id} onChange={(e) => setApplyForm({ ...applyForm, leave_type_id: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required>
                          <option value="">Select an Active Leave Type</option>
                          {activeTypes.map((lt) => <option key={lt.id} value={lt.id}>{`${lt.name} (${lt.code})`}</option>)}
                        </select>
                      )}
                      {(() => {
                        const selectedLt = leaveTypes.find(lt => lt.id === applyForm.leave_type_id);
                        if (!selectedLt) return null;
                        const isNoBal = selectedLt.requires_balance === false || selectedLt.balance_required === false || selectedLt.code === 'LOP';
                        if (isNoBal) {
                          return (
                            <div className="mt-2 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-2.5 text-xs">
                              ℹ️ <strong>{selectedLt.name} ({selectedLt.code})</strong> does not require an available leave balance. Application will be processed as unpaid leave without deducting from paid balances.
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  );
                })()}

                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">From *</label><input type="date" value={applyForm.from_date} onChange={(e) => setApplyForm({ ...applyForm, from_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                  <div><label className="block text-sm font-medium text-slate-700 mb-1">To *</label><input type="date" value={applyForm.to_date} onChange={(e) => setApplyForm({ ...applyForm, to_date: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" required /></div>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className="block text-sm font-medium text-slate-700 mb-1">Total Days</label><input type="number" step="0.5" value={applyForm.total_days} onChange={(e) => setApplyForm({ ...applyForm, total_days: parseFloat(e.target.value) })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                    <div className="flex items-end pb-1"><label className="flex items-center space-x-2"><input type="checkbox" checked={applyForm.is_half_day} onChange={(e) => setApplyForm({ ...applyForm, is_half_day: e.target.checked, half_day: e.target.checked, total_days: e.target.checked ? 0.5 : 1 })} /><span className="text-sm text-slate-700 font-medium">Half Day</span></label></div>
                  </div>

                  {applyForm.is_half_day && (
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-2.5 space-y-1">
                      <label className="block text-xs font-semibold text-slate-700 uppercase">Select Half-Day Period *</label>
                      <div className="grid grid-cols-2 gap-2">
                        <label className={`flex items-center space-x-2 p-2 rounded border text-xs font-semibold cursor-pointer ${applyForm.half_day_period === 'first_half' ? 'bg-blue-50 border-[#0052FF] text-[#0052FF]' : 'bg-white border-slate-200 text-slate-700'}`}>
                          <input type="radio" name="admin_half_day_period" value="first_half" checked={applyForm.half_day_period === 'first_half'} onChange={(e) => setApplyForm({ ...applyForm, half_day_period: e.target.value })} className="w-3.5 h-3.5 text-[#0052FF]" />
                          <span>First Half</span>
                        </label>
                        <label className={`flex items-center space-x-2 p-2 rounded border text-xs font-semibold cursor-pointer ${applyForm.half_day_period === 'second_half' ? 'bg-blue-50 border-[#0052FF] text-[#0052FF]' : 'bg-white border-slate-200 text-slate-700'}`}>
                          <input type="radio" name="admin_half_day_period" value="second_half" checked={applyForm.half_day_period === 'second_half'} onChange={(e) => setApplyForm({ ...applyForm, half_day_period: e.target.value })} className="w-3.5 h-3.5 text-[#0052FF]" />
                          <span>Second Half</span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Reason</label><textarea value={applyForm.reason} onChange={(e) => setApplyForm({ ...applyForm, reason: e.target.value })} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" rows={2} /></div>
                <div className="flex justify-end space-x-3 pt-2">
                  <button type="button" onClick={() => setShowApplyModal(false)} className="px-4 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50">Cancel</button>
                  <button data-testid="submit-leave-btn" type="submit" disabled={leaveTypes.filter(lt => lt.is_active && !lt.is_deleted).length === 0} className="px-4 py-2 text-sm bg-[#0052FF] text-white rounded-lg hover:bg-[#0040CC] font-semibold disabled:opacity-50">Submit</button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>
    </MainLayout>
  );
};

export default LeaveManagement;
